import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCurrency, getConfig } from 'utils/helperMethods';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { FilterContextData } from 'contexts/FilterContext';
import { pricingAnalyticsUrls } from 'config/urls';
import {
    categoryMovementByLevelChartParser,
    categoryMovementChartParser,
} from 'parser/pricingAnalytics/categoryMovementParser';
import { CategoryMovementOutputChartItem, PricingReport } from 'types/pricingSimulator';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import { ConfigKeys } from 'utils/Enums';
import CategoryMovementChartItem from './CategoryMovementChartItem';

const containerId = 'category-movement-chart-container';
const CategoryMovementContainer = () => {
    const { appConfig } = useContext(UserContext);
    const {
        selectedReportInfo,
        updateReportProperty,
        isUpdate,
        filters,
        loggedInUser,
        setSelectedReportInfo,
        showPercentageChange,
    } = useContext(PricingAnalyticsDataContext);
    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );

    const currency = fetchCurrency(appConfig);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const { dim1DropdownList = [], dim2DropdownList = [] } = dimensionConfigData ?? {};

    const { categoryMovementKpis, analyticsChartLimit } = pricingSimulatorConfig;
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const containerWidth = useContainerWidth({
        containerId: containerId,
        buffer: yAxisGraphWidth + 34 * 2 + 112,
    }); //subtracting graph width and padding on 2 sides

    const [selectedChartItems, setSelectedChartItems] = useState([null, null]);
    const [commonVariablesTopChart, setCommonVariablesTopChart] = useState<any>();
    const [commonVariablesBottomChart, setCommonVariablesBottomChart] = useState<any[] | undefined>();
    //inf scroller width ref
    const infScrollerWidthRef = useRef<any[]>([0, 0]);

    //level dropdown list
    const listToDisplay = useMemo(() => {
        return [
            { label: 'Product', options: dim1DropdownList },
            { label: 'Geo', options: dim2DropdownList },
        ];
    }, [dim1DropdownList, dim2DropdownList]);

    //set variables for top charts
    useEffect(() => {
        const topChartVariables = {
            kpi_list: categoryMovementKpis?.map((item: any) => item?.value),
            // user_name: loggedInUser,
            base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
            filters: filters,
            show_percent: showPercentageChange,
        };

        setCommonVariablesTopChart(topChartVariables);
    }, [
        selectedReportInfo?.baseScenario?.scenarioId,
        filters,
        selectedReportInfo?.categoryMovement?.input,
        showPercentageChange,
    ]);

    //set variables for bottom charts
    useEffect(() => {
        //reset inf scroller width
        infScrollerWidthRef.current = [0, 0];

        const bottomChartVariables = selectedReportInfo?.categoryMovement?.input?.map((graphItem, idx) => {
            return {
                impact_kpi_name: graphItem?.bottomChartKpiId,
                user_name: loggedInUser,
                filters: filters,
                impact_level: graphItem?.bottomChartAttributeType?.value,
                is_update: isUpdate,
                base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
                report_id: selectedReportInfo?.reportId,
                feature_name: selectedChartItems?.[idx],
                limit: analyticsChartLimit,
                offset: 0,
                show_percent: showPercentageChange,
            };
        });
        setCommonVariablesBottomChart(bottomChartVariables);
    }, [
        loggedInUser,
        filters,
        selectedChartItems,
        selectedReportInfo?.baseScenario?.scenarioId,
        selectedReportInfo?.categoryMovement?.input,
        showPercentageChange,
    ]);

    //defaulting chart selection to 1st chart item
    useEffect(() => {
        if (selectedReportInfo?.categoryMovement?.output?.topChartItems && selectedChartItems?.includes(null)) {
            const tempSelectedItemsArray: any[] = [];
            selectedReportInfo?.categoryMovement?.output?.topChartItems?.forEach((item) => {
                tempSelectedItemsArray.push(item?.data?.[0]?.featureName ?? null);
            });
            setSelectedChartItems(tempSelectedItemsArray);
        }
    }, [selectedReportInfo?.categoryMovement?.output?.topChartItems]);

    //top charts
    const { loading: topChartsLoading, error: topChartsError } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariablesTopChart?.kpi_list?.length > 0) },
        url: pricingAnalyticsUrls.getCategoryMovementKpiChartDataUrl,
        queryKey: ['pa-category-movement-chart', 'pa-category-movement-chart-top', commonVariablesTopChart],
        variables: { ...(commonVariablesTopChart || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.length) {
                throw 'No Data';
            }
            const outputObj: CategoryMovementOutputChartItem[] = categoryMovementChartParser(categoryMovementKpis, res);

            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    categoryMovement: {
                        input: prevState?.categoryMovement?.input,
                        output: {
                            bottomChartItems: prevState?.categoryMovement?.output?.bottomChartItems,
                            topChartItems: outputObj,
                        },
                    },
                };
            });
            return {};
        },
    });

    //bottom chart 1
    const { loading: bottomChart1Loading, error: bottomChart1Error } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariablesBottomChart?.[0]?.feature_name) },
        url: pricingAnalyticsUrls.getCategoryMovementChartByLevelDataUrl,
        queryKey: [
            'pa-category-movement-chart',
            'pa-category-movement-chart-bottom-1',
            commonVariablesBottomChart?.[0],
        ],
        variables: { ...(commonVariablesBottomChart?.[0] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = categoryMovementByLevelChartParser(
                categoryMovementKpis?.[0],
                res?.total_count,
                res?.data,
            );
            //if inf scroller , then merge data
            if (commonVariablesBottomChart?.[0]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[0]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[0]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[0]?.data ?? []),
                    ...outputObj?.data,
                ];
            }
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    categoryMovement: {
                        input: prevState?.categoryMovement?.input,
                        output: {
                            topChartItems: prevState?.categoryMovement?.output?.topChartItems,
                            bottomChartItems: prevState?.categoryMovement?.output?.bottomChartItems?.[1]
                                ? [outputObj, prevState?.categoryMovement?.output?.bottomChartItems?.[1]]
                                : [outputObj, { data: [] }],
                        },
                    },
                };
            });
            return {};
        },
    });

    //bottom chart 2
    const { loading: bottomChart2Loading, error: bottomChart2Error } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariablesBottomChart?.[1]?.feature_name) },
        url: pricingAnalyticsUrls.getCategoryMovementChartByLevelDataUrl,
        queryKey: [
            'pa-category-movement-chart',
            'pa-category-movement-chart-bottom-1',
            commonVariablesBottomChart?.[1],
        ],
        variables: { ...(commonVariablesBottomChart?.[1] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = categoryMovementByLevelChartParser(
                categoryMovementKpis?.[1],
                res?.total_count,
                res?.data,
            );
            //if inf scroller , then merge data
            if (commonVariablesBottomChart?.[1]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[1]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[1]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[1]?.data ?? []),
                    ...outputObj?.data,
                ];
            }
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    categoryMovement: {
                        input: prevState?.categoryMovement?.input,
                        output: {
                            topChartItems: prevState?.categoryMovement?.output?.topChartItems,
                            bottomChartItems: prevState?.categoryMovement?.output?.bottomChartItems?.[0]
                                ? [prevState?.categoryMovement?.output?.bottomChartItems?.[0], outputObj]
                                : [{ data: [] }, outputObj],
                        },
                    },
                };
            });
            return {};
        },
    });

    const indexLoaderErrorMapping: any = {
        0: [bottomChart1Loading, bottomChart1Error],
        1: [bottomChart2Loading, bottomChart2Error],
    };

    const infiniteScrollOnNext = useCallback(
        (width: number, loading: boolean, index: number) => {
            if (!loading) {
                const newOffset = Number(commonVariablesBottomChart?.[index]?.offset) + analyticsChartLimit;

                //updating width
                infScrollerWidthRef.current.splice(index, 1, width);

                //updating offset
                setCommonVariablesBottomChart((prevValue: any) => {
                    return prevValue?.map((variable: any, idx: number) => {
                        return {
                            ...variable,
                            offset: index === idx ? newOffset : variable?.offset,
                        };
                    });
                });
            }
        },
        [commonVariablesBottomChart, setCommonVariablesBottomChart],
    );

    return (
        <div id={containerId} className="w-full mt-4 simulator-analytics-chart-container">
            <div className="mb-4 grid grid-cols-2 gap-4">
                {/* UPPER CHART CONTAINER */}
                {selectedReportInfo?.categoryMovement?.output?.topChartItems?.map((chartItem, idx) => (
                    <CategoryMovementChartItem
                        key={chartItem?.id}
                        chartItem={chartItem}
                        idx={idx}
                        containerWidth={containerWidth}
                        selectedChartItems={selectedChartItems}
                        setSelectedChartItems={setSelectedChartItems}
                        loading={topChartsLoading}
                        error={topChartsError}
                        currency={currency}
                        isTopChart={true}
                        showLevelDropdown={false}
                        listToDisplay={listToDisplay}
                    />
                ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
                {/* LOWER CHART CONTAINER */}
                {selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.map((chartItem, idx) => (
                    <CategoryMovementChartItem
                        key={chartItem?.id}
                        chartItem={chartItem}
                        idx={idx}
                        containerWidth={containerWidth}
                        selectedChartItems={selectedChartItems}
                        setSelectedChartItems={setSelectedChartItems}
                        loading={indexLoaderErrorMapping?.[idx]?.[0] || topChartsLoading}
                        error={indexLoaderErrorMapping?.[idx]?.[1] || topChartsError}
                        currency={currency}
                        isTopChart={false}
                        showLevelDropdown={true}
                        listToDisplay={listToDisplay}
                    />
                ))}
            </div>
        </div>
    );
};

export default CategoryMovementContainer;
